import { css } from 'lit';
export default css `
    :host {
        --button-box-shadow: var(--surface-elevated);
        --button-border: none;
        --button-color: var(--color-secondary);
        --button-color-hover: var(--color-secondary-dark);
        --button-color-text: var(--color-ui-08);
        --button-cursor: pointer;
        --button-font-family: var(--font-normal);
        --button-font-weight: var(--weight-medium);
        --button-label-width: 100%;
        --button-label-overflow: visible;
        --button-label-whitespace: nowrap;
        --button-text-align: center;
        --button-min-width: 44px;
        --button-padding-vertical: var(--spacing-s);
        --button-padding-horizontal: var(--spacing-xl);
        --button-padding: var(--button-padding-vertical) var(--button-padding-horizontal);
        --button-padding-text: 0 var(--spacing-xs);
        --button-padding-icon: var(--spacing-xxxs) var(--spacing-xs);
        --button-outside-padding: 0;
        --button-radius: var(--radius-l);
        --button-display: inline-block;
        --button-overflow: hidden;

        --icon-color: var(--color-ui-08);

        display: var(--button-display);
        min-width: var(--button-min-width);
        cursor: var(--button-cursor);
        padding: var(--button-outside-padding);
    }

    /*
     * Variants
     */

    :host([variant='primary']) {
        --button-color: var(--color-secondary);
        --button-color-hover: var(--color-secondary-dark);
    }

    :host([variant='secondary']) {
        --button-color: var(--color-primary);
        --button-color-hover: var(--color-primary-dark);
    }

    :host([variant='cta']) {
        --button-color: var(--color-extreme);
        --button-color-hover: var(--color-extreme-dark);
    }

    :host([variant='ghost']) {
        --button-color: var(--color-ui-08);
        --button-color-hover: var(--color-ui-07);
        --button-color-text: var(--color-primary);
        --button-border: 2px solid var(--color-primary);
        --button-padding: calc(var(--button-padding-vertical) - 2px) calc(var(--button-padding-horizontal) - 2px);
        --icon-color: var(--button-color-secondary);
    }

    :host([variant='ghost-thin']) {
        --button-box-shadow: none;
        --button-color: var(--color-ui-08);
        --button-color-hover: var(--color-ui-07);
        --button-color-text: var(--color-primary);
        --button-border: 1px solid var(--color-ui-04);
        --button-padding: 0.5rem;
        --button-radius: 0;
    }

    :host([variant='primary']) .button::before,
    :host([variant='secondary']) .button::before,
    :host([variant='cta']) .button::before,
    :host([variant='ghost']) .button::before,
    :host([variant='ghost-thin']) .button::before {
        background-color: var(--button-color);
        content: '';
        display: block;
        height: 0;
        left: 0;
        position: absolute;
        transition: background-color linear var(--transition-speed), height linear var(--transition-speed);
        bottom: 0;
        width: 100%;
    }

    :host([variant='primary']) .button:hover::before,
    :host([variant='secondary']) .button:hover::before,
    :host([variant='cta']) .button:hover::before,
    :host([variant='ghost']) .button:hover::before,
    :host([variant='ghost-thin']) .button:hover::before {
        background-color: var(--button-color-hover);
        height: 100%;
    }

    :host([variant='primary']) .button:hover,
    :host([variant='secondary']) .button:hover,
    :host([variant='cta']) .button:hover,
    :host([variant='ghost']) .button:hover {
        --button-box-shadow: var(--surface-floating);
    }
    :host([variant='primary']) .button:focus,
    :host([variant='secondary']) .button:focus,
    :host([variant='cta']) .button:focus,
    :host([variant='ghost']) .button:focus {
        --button-box-shadow: var(--surface-floating-focus);
    }

    :host([variant='text']),
    :host([variant='underline']),
    :host([variant='white']),
    :host([variant='icon-only']) {
        --button-box-shadow: none;
        --button-color: transparent;
        --button-font-family: var(--font-normal);
        --button-font-weight: var(--weight-regular);
        --button-padding-horizontal: 0;
        --button-padding-vertical: 0;
        --button-padding-text: 0;
        --button-outside-padding: calc(var(--spacing-xxs) / 2) 0;
        --button-radius: 0;
        --button-min-width: auto;
    }

    :host([variant='text']),
    :host([variant='underline']),
    :host([variant='icon-only']) {
        --button-color-text: var(--color-primary);
        --icon-color: var(--color-primary);
    }

    :host([variant='text']) .button:hover,
    :host([variant='underline']) .button:hover,
    :host([variant='icon-only']) .button:hover {
        --button-color-text: var(--color-primary-dark);
        --icon-color: var(--color-primary-dark);
    }

    :host([variant='underline']) .button {
        text-decoration: underline;
        text-decoration-color: var(--color-primary);
    }

    :host([variant='white']) {
        --button-color-text: var(--color-ui-08);
        --icon-color: var(--color-ui-08);
    }

    :host([variant='white']) .button:hover {
        --button-color-text: var(--color-ui-08);
        --icon-color: var(--color-ui-08);
    }

    :host([variant='icon-only']) {
        --button-padding: 0;
        --button-padding-icon: 0;

        position: relative;
    }

    :host([variant='icon-only'])::after {
        content: '';
        position: absolute;
        width: var(--icon-click-area);
        height: var(--icon-click-area);
        top: -50%;
        left: -50%;
    }

    /*
    * Sizing
    */

    :host([size='small'][variant='primary']),
    :host([size='small'][variant='secondary']),
    :host([size='small'][variant='cta']),
    :host([size='small'][variant='ghost']),
    :host([size='small'][variant='ghost-thin']) {
        --button-min-width: 40px;
        --button-padding-vertical: var(--spacing-xs);
        --button-padding-horizontal: 0.625rem;
        --button-padding-text: 0 0.375rem;
        --button-padding-icon: var(--spacing-xxs) 0.375rem;
        --button-outside-padding: calc(var(--spacing-xxs) / 2) 0;
    }

    :host([empty][size='small'][variant='primary']),
    :host([empty][size='small'][variant='secondary']),
    :host([empty][size='small'][variant='cta']),
    :host([empty][size='small'][variant='ghost']),
    :host([empty][size='small'][variant='ghost-thin']) {
        --button-padding-horizontal: 0.375rem;
        --button-outside-padding: calc(var(--spacing-xxs) / 2);
    }

    :host([size='small'][variant='text']),
    :host([size='small'][variant='underline']),
    :host([size='small'][variant='white']) {
        --button-padding-icon: var(--spacing-xxs) 0;
    }

    /*
    * Alignment
    */

    :host([icon-position='left']) .icon {
        order: 0;
    }

    :host([icon-position='right']) .icon {
        order: 2;
    }

    /*
    * Disabled
    */

    :host([disabled]) {
        --button-box-shadow: none;
        --button-color: var(--color-ui-04);
        --button-color-hover: var(--color-ui-04);
        --button-color-text: var(--color-ui-08);
        --button-cursor: not-allowed;

        pointer-events: none;
    }

    :host([disabled][variant='ghost']) {
        --button-border: 2px solid var(--color-ui-04);
    }

    :host([disabled][variant='ghost-thin']) {
        --button-color: var(--color-ui-08);
        --button-color-text: var(--color-primary);
        opacity: 0.2;
    }

    :host([disabled][variant='text']) {
        --button-color: transparent;
        --button-color-text: var(--color-ui-04);
        --icon-color: var(--color-ui-04);
    }

    /*
    * Inline
    */
    :host([inline]) {
        --button-display: inline-block;
        --button-padding-text: none;
        --button-padding-vertical: none;
        --button-outside-padding: 0;
        --button-text-align: left;
        --button-label-whitespace: normal;
    }

    /*
    * Empty
    */

    :host([empty]) {
        --button-padding-horizontal: var(--spacing-xs);
    }

    :host([empty]) .label {
        display: none;
    }

    :host([screenreader-label]) .label {
        font-size: 0;
        width: 0px;
        height: 0px;
        position: absolute;
    }

    /*
    * Implementation
    */

    .icon {
        display: inline-block;
        padding: var(--button-padding-icon);
        position: relative;
    }

    .button {
        align-items: flex-start;
        background-color: var(--button-color);
        border: var(--button-border);
        border-radius: var(--button-radius);
        box-sizing: border-box;
        box-shadow: var(--button-box-shadow);
        color: var(--button-color-text, #fff);
        cursor: var(--button-cursor, pointer);
        display: inline-flex;
        font-family: var(--button-font-family);
        font-size: 1rem;
        font-weight: var(--button-font-weight);
        justify-content: center;
        line-height: 1;
        overflow: var(--button-overflow);
        padding: var(--button-padding);
        position: relative;
        text-align: var(--button-text-align);
        text-decoration: none;
        transition: color var(--transition-speed), border-color var(--transition-speed),
            box-shadow var(--transition-speed);
        width: 100%;
    }

    .button:focus {
        outline: none;
    }

    .button:focus-visible {
        outline: var(--color-primary-dark) solid;
    }

    .label {
        display: inline-block;
        line-height: 1.5;
        padding: var(--button-padding-text);
        position: relative;
        white-space: var(--button-label-whitespace);
        overflow: var(--button-label-overflow);
        text-overflow: ellipsis;
        width: var(--button-label-width);
    }

    /*
    * Hide label on mobile
    */

    :host([hide-label-on-mobile]) {
        --button-padding-horizontal: var(--spacing-xs);
    }

    :host([hide-label-on-mobile]) .label {
        display: none;
    }

    @media screen and (min-width: 768px) {
        :host([hide-label-on-mobile]) {
            --button-padding-horizontal: var(--spacing-xl);
        }

        :host([hide-label-on-mobile][variant='text']) {
            --button-padding-horizontal: 0;
        }

        :host([hide-label-on-mobile]) .label {
            display: inline-block;
        }
    }

    /* Theme: blue */
    :host([theme='blue']) {
        --icon-color: var(--color-support-02);
        --button-box-shadow: none;
    }

    :host([theme='blue']) .button {
        --button-color: var(--color-ui-08);
        --button-color-text: var(--color-support-02);
    }

    :host([theme='blue']) .button:hover,
    :host([theme='blue']) .button:focus {
        --icon-color: var(--color-ui-08);
        --button-color-text: var(--color-ui-08);
        --button-box-shadow: none;
    }

    :host([theme='blue']) .button:focus {
        --button-color: var(--color-primary);
    }

    :host([theme='blue'][variant='secondary']) {
        --button-border: 2px solid var(--color-ui-08);
    }

    :host([theme='blue'][variant='text']) .button,
    :host([theme='blue'][variant='underline']) .button,
    :host([theme='blue'][variant='icon-only']) .button {
        --icon-color: var(--color-ui-08);
        --button-color: transparent;
        --button-color-text: var(--color-ui-08);
    }

    :host([theme='blue'][variant='underline']) .button {
        text-decoration-color: var(--color-ui-08);
    }

    :host([theme='blue'][variant='underline']) .button:hover {
        text-decoration: none;
    }
`;
